import classNames from "classnames";
import * as moment from "moment";
import { ECalendarName } from "motw_core/Calendar/ECalendarName";
import * as React from "react";
import { CalendarEvent } from "source/app/presentation/MainPanel/Calendar/CalendarEventUtils/CalendarEvent";
import * as styles from "source/app/presentation/MainPanel/Calendar/CalendarEvents/CalendarEvents.css";
import { LaunchCalendarEventEditor } from "source/app/presentation/MainPanel/Calendar/CalendarEvents/Render/Editor/LaunchCalendarEventEditor";
import { ShowGoalIcon } from "source/app/presentation/MainPanel/Calendar/CalendarEvents/Render/GoalIcon/ShowGoalIcon";
import { RepeatingIcon } from "source/app/presentation/MainPanel/Calendar/CalendarEvents/Render/RepeatingIcon/RepeatingIcon";
import { CalendarNameBadge } from "source/app/presentation/MainPanel/Calendar/CalendarNameBadge/CalendarNameBadge";
import { CalendarEventDoneSwitch } from "source/app/presentation/MainPanel/Calendar/DoneSwitch/CalendarEventDoneSwitch";
import Cell from "source/app/presentation/MainPanel/Dreams/Cell/Cell";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Glyphicon from "source/app/presentation/Shared/Glyphicon/Glyphicon";
import Hyperlink from "source/app/presentation/Shared/Link/Hyperlink";
import Render from "source/app/presentation/Shared/Render";
import { EDragDropObject } from "source/app/services/Shared/EDragDropObject";
import { useDragSource } from "source/app/services/Shared/Hooks/useDragSource";
import { ICalendarState } from "../../ICalendarState";
import { CalendarEventDropTarget } from "./CalendarEventDropTarget";
import { ICalendarEvent } from "motw_core/Calendar/ICalendarEvent";
import { AreEqual } from "source/app/services/Shared/ReactHelpers";
import IconWithText from "source/app/services/Shared/IconWithText/IconWithText";
import { DeleteCalendarEventActionAsync } from "../../CalendarEventDoneSwitch/DeleteEventLink/DeleteCalendarEventActionAsync";

const RenderCalendarEvent = React.memo(function RenderCalendarEvent({
    hoursBeingChanged,
    eventData,
}: {
    hoursBeingChanged: number[];
    eventData: ICalendarEvent;
}) {
    const calendarEvent = new CalendarEvent(eventData);

    const divId =
        calendarEvent.IsHappeningNow && !calendarEvent.IsAllDay
            ? `scrollTarget_calendarEventNow`
            : ``;

    const dragSourceResult = useDragSource(
        EDragDropObject.calendarEvent,
        calendarEvent
    );

    const dragSourceRef = calendarEvent.BelongsToCalendar(ECalendarName.Free)
        ? null
        : dragSourceResult.dragSourceRef;

    const occupiedHourNumbers = calendarEvent.OccupiedHourNumbers();

    const isBeingChanged =
        occupiedHourNumbers.filter((hourNumber) =>
            hoursBeingChanged.includes(hourNumber)
        ).length > 0;

    return (
        <div
            id={divId}
            key={`${eventData.name}_${eventData.id}`}
            ref={dragSourceRef}
            className={classNames([styles.event], {
                [styles.isBeingChanged]: isBeingChanged,
                [styles.eventNotToday]: !calendarEvent.IsToday,
                [styles.currentEvent]: calendarEvent.IsHappeningNow,
                [styles.reminder]: calendarEvent.BelongsToCalendar(
                    ECalendarName.Reminders
                ),
                [styles.free]: calendarEvent.BelongsToCalendar(
                    ECalendarName.Free
                ),
                [styles.completedEvent]: calendarEvent.IsCompleted,
                [styles.allDayEvent]:
                    calendarEvent.IsAllDay && !calendarEvent.IsCompleted,
                [styles.isInThePast]: calendarEvent.HasFinished,
                [styles.needsToBeBlocked]:
                    calendarEvent.StartingInMinutes <= 120 &&
                    calendarEvent.BelongsToCalendar(ECalendarName.Free),
            })}
        >
            <div>
                <Render
                    id="calendarEvent_nowIcon"
                    when={calendarEvent.IsHappeningNow}
                >
                    <Glyphicon
                        icon={EGlyphicon.play}
                        withClass={styles.nowIcon}
                    />
                </Render>
                <ShowStartTime {...{ calendarEvent }} />
            </div>
            <div>
                <Hyperlink
                    whenClicked={() => LaunchCalendarEventEditor(calendarEvent)}
                >
                    {eventData.name}
                    <CalendarEventDropTarget {...{ calendarEvent }} />
                    <CalendarNameBadge calendarName={eventData.calendar} />
                    <RepeatingIcon isRepeating={eventData.isRepeating} />
                </Hyperlink>
                <IconWithText
                    whenClicked={() =>
                        DeleteCalendarEventActionAsync(calendarEvent.eventData)
                    }
                    iconSuffix={EGlyphicon.trash}
                    displayAsBlock={false}
                />
                <Cell>
                    <ShowGoalIcon matchingGoal={eventData.matchingGoal} />
                </Cell>
                <CalendarEventDoneSwitch
                    {...{
                        eventData,
                        key: eventData.completionEventId,
                    }}
                />
                <DisplayStartEndTime calendarEvent={calendarEvent} />
                <DisplayDuration calendarEvent={calendarEvent} />
            </div>
        </div>
    );
},
AreEqual);

export { RenderCalendarEvent };

function ShowStartTime({ calendarEvent }: { calendarEvent: CalendarEvent }) {
    if (calendarEvent.IsAllDay) {
        return <span />;
    }

    return (
        <span>{CalendarEvent.FormatTime(calendarEvent.eventData.start)}</span>
    );
}

function DisplayStartEndTime({
    calendarEvent,
}: {
    calendarEvent: CalendarEvent;
}) {
    if (calendarEvent.IsAllDay) {
        return <div style={{ opacity: 0.3 }}>All Day</div>;
    }

    const startEndTimeText = `from ${CalendarEvent.FormatTime(
        calendarEvent.eventData.start
    )} to ${CalendarEvent.FormatTime(calendarEvent.eventData.end)}`;

    const timeLeftText = moment(calendarEvent.eventData.end).fromNow(true);

    const timeRemainingText = calendarEvent.IsHappeningNow
        ? ` - ${timeLeftText} remaining`
        : ``;

    const textToDisplay = `${startEndTimeText}${timeRemainingText}`;
    return <Cell classes={[styles.fromToTimeText]}>{textToDisplay}</Cell>;
}

function DisplayDuration({ calendarEvent }: { calendarEvent: CalendarEvent }) {
    if (calendarEvent.IsAllDay) {
        return null;
    }

    const height =
        calendarEvent.DurationInMinutes <= 60
            ? 0
            : (calendarEvent.DurationInMinutes / 60) * 25;

    return <div style={{ height: `${height}px` }}></div>;
}
